import { Button, Field } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { ArrowRightIcon } from "@eisox/icons";
import { getDirtyValues } from "@eisox/tools";
import { z } from "@eisox/zod";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { airHandlerUnitApi } from "~/api";
import { MoveItemDialog } from "~/components/move-item-dialog";
import { useAirHandlerUnitContext, useUpdateAirHandlerUnit } from "~/features/air-handler-unit";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute(
  "/_authenticated/buildings_/$buildingId/air-handler-units/$airHandlerUnitId/settings/general",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { buildingId } = Route.useParams();
  const { airHandlerUnit, isGroup, name } = useAirHandlerUnitContext(Route.id);
  const { floors } = useBuildingContext(Route.id);

  const { mutateAsync } = useUpdateAirHandlerUnit(buildingId);

  // The move dialog works on the building list DTO (floor position), already loaded by the route loader.
  const groupIds = airHandlerUnit.map(ahu => ahu.id);
  const { data: buildingAirHandlerUnits } = useQuery(airHandlerUnitApi.queries.building(buildingId));
  const moveItems = (buildingAirHandlerUnits ?? []).filter(ahu => groupIds.includes(ahu.id));

  const form = useAppForm({
    defaultValues: {
      group: isGroup ? name : undefined,
      airHandlerUnits: airHandlerUnit.map(ahu => ({
        id: ahu.id,
        name: ahu.name,
      })),
    },
    validators: {
      onSubmit: z.object({
        group: isGroup ? z.string().nonempty() : z.string().nonempty().optional(),
        airHandlerUnits: z.array(
          z.object({
            id: z.string().nonempty(),
            name: z.string().nonempty(),
          }),
        ),
      }),
    },
    onSubmit: async ({ formApi, value }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value, { identity: "id" });
      if (!dirty) return;
      const patches =
        dirty.group !== undefined
          ? value.airHandlerUnits.map(ahu => ({
              airHandlerUnitId: ahu.id,
              body: { name: ahu.name, group: value.group },
            }))
          : (dirty.airHandlerUnits ?? []).map(ahu => ({
              airHandlerUnitId: ahu.id,
              body: { name: ahu.name },
            }));
      await Promise.all(patches.map(patch => mutateAsync(patch)));
      formApi.reset(value);
    },
  });

  return (
    <form
      className="gap-5 flex h-full flex-col"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        {isGroup && (
          <form.AppField name="group">
            {field => (
              <field.TextField
                classNames={{ label: "text-primary-blue", control: "w-full max-w-[300px]" }}
                label={m.group_name()}
              />
            )}
          </form.AppField>
        )}
        <form.AppField name="airHandlerUnits" mode="array">
          {field =>
            field.state.value.map((_, i) => (
              <form.AppField key={i} name={`airHandlerUnits[${i}].name`}>
                {field => (
                  <field.TextField
                    classNames={{ label: "text-primary-blue", control: "w-full max-w-[300px]" }}
                    label={m.name()}
                  />
                )}
              </form.AppField>
            ))
          }
        </form.AppField>
        <Field.Root>
          <Field.Label className="text-primary-blue">{m.position()}</Field.Label>
          <MoveItemDialog type="airHandlerUnit" buildingId={buildingId} items={moveItems} floors={floors}>
            <Button className="text-primary-dark-blue" type="button">
              {m.move()} <ArrowRightIcon />
            </Button>
          </MoveItemDialog>
        </Field.Root>
        <form.SubmitButton className="mt-auto self-center" label={m.save()} />
      </form.AppForm>
    </form>
  );
}
